// Helpers operating on canonical FlightOffer.travelerPricings[] entries
// and the form-side `traveler_details` shape. Pure transforms / lookups.

import moment from 'moment';

import { log } from '@/utilities/commons';

const TRAVELER_TYPES = {
  ADULT: 'adult',
  CHILD: 'child',
  TEEN: 'teen',
  HELD_INFANT: 'held_infant',
  SEATED_INFANT: 'seated_infant',
};

const INFANT_TYPES = new Set([
  TRAVELER_TYPES.HELD_INFANT,
  TRAVELER_TYPES.SEATED_INFANT,
]);

export const traveler_type = (raw) => {
  const traveler = raw || {};
  return String(
    traveler.traveler_type || traveler.travelerType || traveler.type
  ).toLowerCase();
};

export const traveler_name = (traveler) => {
  if (!traveler) {
    return '';
  }
  const { firstname, lastname, name, id } = traveler;
  if (firstname && lastname) {
    return `${firstname} ${lastname}`;
  }
  if (name) {
    return Object.values(name).join(' ');
  }
  return `P${id}`;
};

export const is_infant = (traveler) =>
  !!traveler && INFANT_TYPES.has(traveler_type(traveler));

export const is_child = (traveler) =>
  !!traveler && traveler_type(traveler) === TRAVELER_TYPES.CHILD;

export const is_teen = (traveler) =>
  !!traveler && traveler_type(traveler) === TRAVELER_TYPES.TEEN;

export const is_adult = (traveler) =>
  !!traveler && traveler_type(traveler) === TRAVELER_TYPES.ADULT;

/**
 * Shallow copy with every string field trimmed of edge whitespace. Pasted
 * names, emails and phone numbers routinely carry leading/trailing spaces
 * that Amadeus /orders and Kyte /book reject at PNR creation. Run once at
 * checkout submit — never bind it live (v-model.trim strips spaces mid-word,
 * breaking multi-token names like "De La Cruz").
 *
 * @param {object} record a traveler_details entry or contact_details
 * @returns {object}
 */
export const trim_traveler_strings = (record) => {
  const safe = { ...(record || {}) };
  return Object.entries(safe).reduce((trimmed, entry) => {
    const [key, value] = entry;
    if (typeof value === 'string') {
      trimmed[key] = value.trim();
    }
    return trimmed;
  }, safe);
};

/**
 * Assign an adult passenger to the associatedAdultId of an infant.
 * @param {FlightOfferObj} flight_offer The initial flight offer
 * @param {{[infantId: string]: string}} heldPassengers a map linking infantId to associatedAdultId
 * @returns A copy of the original flight_offer with the updated infant
 */
export function set_infant_associated_adult_id(flight_offer, heldPassengers) {
  if (!heldPassengers || !flight_offer) {
    return flight_offer;
  }
  const { travelerPricings } = flight_offer;
  if (!travelerPricings) {
    return flight_offer;
  }
  return {
    ...flight_offer,
    travelerPricings: travelerPricings.map((pricing) => {
      const associatedAdultId = heldPassengers[pricing.travelerId];
      if (!associatedAdultId) {
        return pricing;
      }
      if (!is_infant(pricing)) {
        log('Trying to assign associatedAdultId for a non infant passenger');
        return pricing;
      }
      return {
        ...pricing,
        associatedAdultId,
      };
    }),
  };
}

/**
 * Italian label for a traveler type/count combination.
 * @param {object} traveler
 * @param {number} [traveler_quantity=1]
 * @returns {string}
 */
export function transcode_traveler_type(traveler, traveler_quantity = 1) {
  const plural = traveler_quantity !== 1;
  if (is_child(traveler)) {
    return plural ? 'bambini' : 'bambino';
  } else if (is_infant(traveler)) {
    return plural ? 'neonati' : 'neonato';
  } else if (is_teen(traveler)) {
    return plural ? 'adolescenti' : 'adolescente';
  }
  return plural ? 'adulti' : 'adulto';
}

/**
 * Birth-date picker bounds for a given traveler. Anchored to the last
 * leg's departure so age requirements hold for the entire trip; today is
 * a last-resort fallback when the offer hasn't been normalized yet.
 *   - HELD_INFANT / SEATED_INFANT → (anchor - 2y + 1d) .. (anchor - 2d)
 *   - CHILD                       → (anchor - 12y - 1d) .. (anchor - 2d)
 *   - TEEN (12–16)                → (anchor - 16y - 1d) .. (anchor - 12y - 1d)
 *   - ADULT (default)             → 1900-01-01 .. (anchor - 12y - 2d),
 *                                   primo pax: (anchor - 18y - 2d)
 * @param {object} flight_offer
 * @param {object} traveler
 * @param {number} [traveler_index=-1] index in traveler_details; index 0 must be 18+
 * @returns {{min: string, max: string}}
 */
export function get_traveler_birth_range_dates(
  flight_offer,
  traveler,
  traveler_index = -1
) {
  const { lastDeparture = {} } = flight_offer || {};
  let departure_date = new Date();
  if (lastDeparture.at) {
    departure_date = lastDeparture.at;
  }

  if (is_infant(traveler)) {
    return {
      max: moment(departure_date).add(-2, 'd').format('YYYY-MM-DD'),
      min: moment(departure_date).add(-2, 'y').add(1, 'd').format('YYYY-MM-DD'),
    };
  }

  if (is_child(traveler)) {
    return {
      max: moment(departure_date).add(-2, 'd').format('YYYY-MM-DD'),
      min: moment(departure_date)
        .add(-12, 'y')
        .add(-1, 'd')
        .format('YYYY-MM-DD'),
    };
  }

  if (is_teen(traveler)) {
    return {
      max: moment(departure_date)
        .add(-12, 'y')
        .add(-1, 'd')
        .format('YYYY-MM-DD'),
      min: moment(departure_date)
        .add(-16, 'y')
        .add(-1, 'd')
        .format('YYYY-MM-DD'),
    };
  }

  // ADULT (default) — il primo pax deve necessariamente essere maggiorenne
  let years_offset = -12;
  if (traveler_index === 0) {
    years_offset = -18;
  }
  return {
    max: moment(departure_date)
      .add(years_offset, 'y')
      .add(-2, 'd')
      .format('YYYY-MM-DD'),
    min: '1900-01-01',
  };
}
